import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { SendMessageResponse, chatQueryKeys } from '../api/chat';
import { ConversationDetailResponse, conversationQueryKeys } from '../api/conversations';
import { useChatStore } from '../stores/chatStore';
import { useConversationDetail } from './useConversationDetail';
import { useShallow } from 'zustand/react/shallow';
import { useSendMessageWithCustomHistory } from './useSendMessage';
import { useStreamingStore } from '../stores/streamingStore';
import { useFileDelete } from './useFileDelete';


interface EditMessageParams {
  conversationId: string;
  messageId: string;
  newContent: string;
  onSuccess?: (response: SendMessageResponse) => void;
  onError?: (error: Error) => void;
}


/**
 * Custom hook for editing messages in a conversation and getting a new response
 *
 * @param conversationId - ID of the conversation containing the message
 * @returns Functions for handling message edits and related state
 */
export function useEditMessage(conversationId: string) {
  const queryClient = useQueryClient();
  const { endStream } = useStreamingStore();

  // AbortController is managed via chatStore to allow cross-component cancellation

  // Use conversation detail query
  const { data: conversation, isLoading: isLoadingConversation } = useConversationDetail(conversationId);

  // Pass skipInvalidate to avoid race refetch while editing
  const { deleteFiles } = useFileDelete(conversationId, { skipInvalidate: true });

  // Use custom history message sender specifically for edits
  const {
    sendMessageWithCustomHistory,
    isLoading: isSendingMessage,
    error: sendMessageError,
    canSend,
    reset
  } = useSendMessageWithCustomHistory(conversationId);

  // Get selected model and abort controller setter from chat store
  const { getSelectedModel, setEditAbortController } = useChatStore(useShallow((state) => ({
    getSelectedModel: state.getSelectedModel,
    setEditAbortController: state.setEditAbortController
  })));


  // API mutation for editing a message
  const { mutate, isPending: isLocalLoading, error, reset: resetMutation } = useMutation({
    meta: { type: 'chat' },
    mutationFn: async ({ messageId, newContent, abortController }: Omit<EditMessageParams, 'conversationId'> & { abortController: AbortController }) => {
      // Ensure we have an AbortController for this request
      if (!abortController) {
        throw new Error('AbortController not available for request cancellation');
      }

      // Use our specialized message edit function with the edited message ID
      const response = await sendMessageWithCustomHistory(
        newContent,
        {
          model: getSelectedModel(conversationId),
          messageToReplaceId: messageId,
          currentStreamId: messageId.replace(/-user$/, ''),
          abortController
        }
      );

      // Parse the response
      const data = await response;

      // Return the API response with proper typing
      return {
        message_id: data.message_id || messageId,
        answer: data.answer || '',
        data_points: data.data_points || [],
        thoughts: data.thoughts,
        error_code: data.error_code,
        agent_classification: data.agent_classification,
        genie_conversation_ids: data.genie_conversation_ids,
        websearch_conversation_id: data.websearch_conversation_id,
        conversationID: data.conversationID || conversationId,
        user_input: data.user_input || newContent,
        response_time: data.response_time,
        token_count: data.token_count,
        toolCalls: data.toolCalls || []
      };
    },

    onSuccess: async (response, variables) => {
      // Clean up AbortController reference on success
      setEditAbortController(null);

      // Update the cache with the response
      await endStream()
      queryClient.setQueryData(
        conversationQueryKeys.detail(conversationId),
        (oldData: any) => {
          if (!oldData) return oldData;

          // Create a deep copy to avoid mutation
          const newData = JSON.parse(JSON.stringify(oldData));

          // Find the message that was edited
          const baseMessageId = variables.messageId.replace(/-user$/, '');
          // Use clean message ID for matching
          const messageIndex = newData.conversationHistory.findIndex(
            (msg: any) => msg.message_id === baseMessageId
          );

          if (messageIndex >= 0) {
            // Update the message with the edited content and response
            newData.conversationHistory[messageIndex].user = variables.newContent;
            newData.conversationHistory[messageIndex].bot = response.answer;

            // Update additional metadata if available
            if (response.token_count) {
              newData.conversationHistory[messageIndex].token_count = response.token_count;
            } else {
              newData.conversationHistory[messageIndex].token_count = 0;
            }

            // Always update data_points (citations) - either with new data or empty array
            newData.conversationHistory[messageIndex].data_points = response.data_points || [];
            // Always update thoughts - either with new data or null
            newData.conversationHistory[messageIndex].thoughts = response.thoughts || null;

            // Always update agent classification - either with new data or null
            newData.conversationHistory[messageIndex].agent_classification = response.agent_classification || null;

            // Persist tool calls from streaming
            newData.conversationHistory[messageIndex].toolCalls = response.toolCalls || [];

            newData.conversationHistory[messageIndex].message_id = response.message_id || null;
          }

          return newData;
        }
      );

      // Invalidate queries to refresh data after the update
      // This logic is to stop refetching the data before it has been set in the backend
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: conversationQueryKeys.detail(conversationId),
          // Prevent automatic refetch right away, as we've already updated the cache
          refetchType: 'none'
        });
        queryClient.invalidateQueries({
          queryKey: chatQueryKeys.messages(conversationId),
          refetchType: 'none'
        });
      }, 500);
    },

    onError: (error: Error) => {
      // Clean up AbortController reference on error
      setEditAbortController(null);
      endStream();

      // Handle AbortError specifically - don't log as error
      const isAbortError = error.name === 'AbortError' ||
                          error.message.includes('aborted') ||
                          error.message.includes('cancelled') ||
                          error.message.includes('REQUEST_CANCELLED');

      if (!isAbortError) {
        console.error('Edit message error:', error);
      } else {
        console.log('Edit message cancelled by user');
      }
    },
  });


  // AbortController is created per edit request within editMessage and stored in chatStore

  // Expose abort functionality
  const abortRequest = useCallback(() => {
    const controller = useChatStore.getState().editAbortController;
    if (controller) {
      controller.abort();
      useChatStore.getState().setEditAbortController(null);
    }
    // Reset the mutation state to clear pending status
    resetMutation();
    // End streaming to stop any ongoing streaming
    endStream();
  }, [resetMutation, endStream]);


  // Helper: analyze conversation data for an edit operation
  const analyzeConversationForEdit = useCallback((currentConversation: any, messageId: string, baseMessageId: string) => {
    const history =
      currentConversation &&
      typeof currentConversation === 'object' &&
      'conversationHistory' in currentConversation
        ? (currentConversation as ConversationDetailResponse).conversationHistory
        : [];

    // Find the index of the message being edited
    const messageIndex = history.findIndex(
      (msg: any) => msg.message_id === messageId || msg.message_id === baseMessageId
    );

    // Messages that will be cut out (after the edited message)
    const cutOutMessages = messageIndex >= 0 ? history.slice(messageIndex + 1) : [];
    const filesToDelete = cutOutMessages.flatMap((msg: any) => msg.uploadedFiles || []);

    // Helper to build / detect the reference prompt line used in ChatInputSimple
    const buildReferenceMessage = (files: string[]) =>
      files.length === 0
        ? ''
        : `Please reference the following document(s) in your response (${files.join(', ')})`;
    const isReferenceMessage = (text: string) =>
      text.startsWith('Please reference the following document(s) in your response (');

    return { history, messageIndex, cutOutMessages, filesToDelete, buildReferenceMessage, isReferenceMessage };
  }, []);


  //* Main function to handle editing a message
  const editMessage = useCallback(
    async ({ messageId, newContent, onSuccess, onError }: Omit<EditMessageParams, 'conversationId'>) => {
      if (!newContent.trim() || isLocalLoading) return;

      // Create AbortController for this request and store it globally for cross-component abort
      const controller = new AbortController();
      setEditAbortController(controller);

      // Loading state is now managed by the TanStack Query mutation

      const messageContent = newContent.trim();

      // Handle message IDs with -user suffix by stripping it off for comparison
      const baseMessageId = messageId.replace(/-user$/, '');

      // Get the current selected model at edit time for consistency
      const currentSelectedModel = useChatStore.getState().getSelectedModel(conversationId);

      const currentConversation = queryClient.getQueryData(conversationQueryKeys.detail(conversationId));
      const {
        history,
        messageIndex,
        cutOutMessages,
        filesToDelete,
        buildReferenceMessage,
        isReferenceMessage,
      } = analyzeConversationForEdit(currentConversation, messageId, baseMessageId);

      //* Update the local state
      try {
        // Cancel any pending queries to ensure we get fresh data after the update
        await queryClient.cancelQueries({
          queryKey: conversationQueryKeys.detail(conversationId)
        });

        // Snapshot previous conversation for rollback on error/abort
        const previousConversation = queryClient.getQueryData(
          conversationQueryKeys.detail(conversationId)
        );

        // Optimistic update via queryClient
        queryClient.setQueryData(
          conversationQueryKeys.detail(conversationId),
          (oldData: any) => {
            if (!oldData) return oldData;

            // Create a deep copy to avoid mutation
            const newData = JSON.parse(JSON.stringify(oldData));

            // Find and update the message with the new content
            if (newData.conversationHistory) {
              const messageIndex = newData.conversationHistory.findIndex(
                (msg: any) => msg.message_id === messageId || msg.message_id === baseMessageId
              );

              if (messageIndex !== -1) {
                // Update user message content
                newData.conversationHistory[messageIndex].gptVersion = currentSelectedModel;

                newData.conversationHistory[messageIndex].user = messageContent;
                newData.conversationHistory[messageIndex].bot = '';
                newData.conversationHistory[messageIndex].data_points = [];
                newData.conversationHistory[messageIndex].token_count = 0;
                newData.conversationHistory[messageIndex].thoughts = null;

                // If this is not the last message, clear all messages under
                if (messageIndex < newData.conversationHistory.length - 1) {
                  newData.conversationHistory = newData.conversationHistory.slice(0, messageIndex + 1);
                }
              }
            }

            return newData;
          }
        );

        if (filesToDelete.length > 0) {
          // Optimistically remove files from the local chat store so the input reference line updates immediately
          const chatStore = useChatStore.getState();
          filesToDelete.forEach((filename: string) => {
            try {
              chatStore.removeConversationFile(conversationId, filename);
              chatStore.removePendingFileForMessage(conversationId, filename);
            } catch (e) {
              console.warn('Failed to optimistically remove file from store:', filename, e);
            }
          });

            // If the current input is the auto-generated reference message, rebuild it (or clear if none)
          const currentInput = chatStore.getInputValue(conversationId);
          if (isReferenceMessage(currentInput)) {
            const remaining = chatStore.getConversationFiles(conversationId);
            const newLine = buildReferenceMessage(remaining);
            chatStore.setInputValue(conversationId, newLine);
          }

          // Proceed with actual deletion request (server)
          deleteFiles({ conversationId, filenames: filesToDelete });
        }

        // Call the mutation
        mutate(
          { messageId, newContent: messageContent, abortController: controller },
          {
            onSuccess: (response: SendMessageResponse) => {
              // Clean up AbortController reference on success
              setEditAbortController(null);
              // Call onSuccess callback if provided
              onSuccess?.(response);
              // Loading state is automatically managed by TanStack Query
            },
            onError: (error: Error) => {
              // Clean up AbortController reference on error
              setEditAbortController(null);
              // Roll back optimistic update on error/abort
              if (previousConversation) {
                queryClient.setQueryData(
                  conversationQueryKeys.detail(conversationId),
                  previousConversation as any
                );
              }
              console.error('Failed to edit message:', error);
              // Call onError callback if provided
              onError?.(error);
              // Loading state is automatically managed by TanStack Query
            },
          }
        );
      } catch (error) {
        // Clean up AbortController reference on error
        setEditAbortController(null);
        console.error('Failed to edit message:', error);
        // Call onError callback if provided
        onError?.(error instanceof Error ? error : new Error('Unknown error'));
        // Loading state is automatically managed by TanStack Query
      }
    },
    [
      isLocalLoading,
      mutate,
      conversationId,
      queryClient,
      setEditAbortController
    ]
  );

  return {
    editMessage,
    isLoading: isLocalLoading,
    isLoadingConversation,
    error,
    conversation,
    abortRequest // Expose abort functionality
  };
}
